import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { dashboardApi, type DashboardOverviewResponse, type DashboardPeriod } from "@/lib/api/dashboard";

export function useDashboard() {
    const { getToken, isLoaded, isSignedIn } = useAuth();
    const [data, setData] = useState<DashboardOverviewResponse | null>(null);
    const [period, setPeriod] = useState<DashboardPeriod>("30d");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchDashboard = useCallback(async () => {
        if (!isLoaded || !isSignedIn) return;

        setIsLoading(true);
        setError(null);

        try {
            const token = await getToken();
            if (!token) {
                throw new Error("No authentication token available");
            }

            const result = await dashboardApi.getOverview(token, period);
            setData(result);
        } catch (err) {
            console.error("Failed to fetch dashboard data:", err);
            setError(err instanceof Error ? err : new Error("Failed to fetch dashboard data"));
        } finally {
            setIsLoading(false);
        }
    }, [isLoaded, isSignedIn, getToken, period]);

    useEffect(() => {
        if (isLoaded && isSignedIn) {
            fetchDashboard();
        }
    }, [isLoaded, isSignedIn, fetchDashboard]);

    return {
        data,
        isLoading: isLoading && isLoaded, // Only show loading if auth is also loaded
        error,
        refetch: fetchDashboard,
        period,
        setPeriod,
    };
}
